import React, { useState } from "react";
import { MenuItem } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import {
  removeTemplateNode,
  copyTemplateNode,
  insertNewFolder,
  insertNewFile,
  pasteTemplateNode,
  setContextAnchor,
} from "src/redux/actions/FlightBuilder.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { RenameNodeModal } from "./RenameNodeModal";
import { openSnackbar } from "src/redux/actions/App.Actions";

interface ITemplateNodeContextMenuProps {
  setBuildFolderDialogOpen?: (state: boolean) => void;
  disableBuildFolder?: boolean;
  children?: any;
}

/**
 * TemplateNodeContextMenu - React hook component to display the context menu items for a flight template node.
 * @param props - the properties described above that get passed into this component
 */
export const TemplateNodeContextMenu = (props: ITemplateNodeContextMenuProps): JSX.Element => {
  const { setBuildFolderDialogOpen, disableBuildFolder } = props;
  const [isRenameDialogShowing, setIsRenameDialogShowing] = useState<boolean>(false);
  const dispatch = useDispatch();
  const activeNode = useSelector((state: IRootState) => state.flightBuilderTemplates.activeNode);
  const copiedNode = useSelector((state: IRootState) => state.flightBuilderTemplates.copiedNode);
  const copiedNodeName: string = copiedNode?.fileName ?? "";

  /**
   * handleMenuItemClick - calls the appropriate captured function then closes the menu
   * @param menuFunction - a function  bound by the MenuItem component to be called before closing the menu
   */
  const handleMenuItemClick = (menuFunction: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    menuFunction();
  };

  /**
   * renameNode - renames the node
   */
  const renameNode = (): void => {
    if (activeNode) {
      setIsRenameDialogShowing(true);
    }
  };

  /**
   * copyNode - Copies the node and saves it in redux, so user can paste node elsewhere
   */
  const copyNode = (): void => {
    dispatch(copyTemplateNode());
    dispatch(setContextAnchor(null)); // close menu
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 1000,
        variant: "success",
        message: "Template Copied",
      })
    );
  };

  /**
   * pasteNode - Pastes the node copied from redux into the heirarchy
   */
  const pasteNode = (): void => {
    dispatch(pasteTemplateNode());
    dispatch(setContextAnchor(null)); // close menu
  };

  /**
   * removeNode - Deletes the current node
   */
  const removeNode = (): void => {
    dispatch(removeTemplateNode());
    dispatch(setContextAnchor(null)); // close menu
  };

  /**
   * build folder - Allows a user to build all the templates in a folder
   */
  const buildFolder = (): void => {
    setBuildFolderDialogOpen(true);
    dispatch(setContextAnchor(null)); // close menu
  };

  /**
   * addNewFolder - appends a new template folder in the tree.
   * If a directory is selected, appends the new folder as a child,
   * If a file is selected appends the folder as a sibling to the file
   * If nothing is selected appends to root
   */
  const addNewFolder = (): void => {
    dispatch(insertNewFolder());
    dispatch(setContextAnchor(null)); // close menu
  };

  /**
   * addNewTemplate - appends a new template file in the tree.
   * Same as above but with files
   */
  const addNewTemplate = (): void => {
    dispatch(insertNewFile());
    dispatch(setContextAnchor(null)); // close menu
  };

  /**
   * Directory has a few more menu items so add them here
   */
  const getDirectoryMenuItems = (): JSX.Element[] => {
    return [
      <MenuItem key={0} onClick={handleMenuItemClick(buildFolder)} disabled={disableBuildFolder}>
        Build Folder
      </MenuItem>,
      <MenuItem key={1} onClick={handleMenuItemClick(addNewFolder)}>
        Add Folder
      </MenuItem>,
      <MenuItem key={2} onClick={handleMenuItemClick(addNewTemplate)}>
        Add New Template
      </MenuItem>,
    ];
  };

  if (!activeNode && !copiedNode) {
    return <MenuItem>Nothing to do</MenuItem>;
  }

  // If there isn't a node attached all you can do is paste node to the root
  if (!activeNode && !!copiedNode) {
    return <MenuItem onClick={handleMenuItemClick(pasteNode)}>Paste ({copiedNodeName})</MenuItem>;
  }

  return (
    <>
      {!activeNode.isLeafNode ? getDirectoryMenuItems() : null}
      <MenuItem key={3} onClick={handleMenuItemClick(renameNode)}>
        Rename
      </MenuItem>
      <MenuItem key={4} onClick={handleMenuItemClick(copyNode)}>
        Copy
      </MenuItem>
      {!!copiedNode && !activeNode?.isLeafNode && (
        <MenuItem key={5} onClick={handleMenuItemClick(pasteNode)}>
          Paste ({copiedNodeName})
        </MenuItem>
      )}
      <MenuItem key={6} onClick={handleMenuItemClick(removeNode)}>
        Delete
      </MenuItem>
      <RenameNodeModal
        open={isRenameDialogShowing}
        closeModal={() => setIsRenameDialogShowing(false)}
        activeNode={activeNode}
      />
    </>
  );
};
